import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';

import {DatePipe, NgClass, NgIf} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatOption} from "@angular/material/autocomplete";
import {MatFormField, MatHint, MatSelect, MatSuffix} from "@angular/material/select";
import {MatInput} from "@angular/material/input";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateAdapter
} from '@angular/material/core';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule} from "@angular/material/form-field";
import {MatIcon, MatIconRegistry} from "@angular/material/icon";
import {CommentModel} from "../../../core/models/comment.model";
import {DomSanitizer} from "@angular/platform-browser";
import {Subscription} from "rxjs";
import {MatButton} from "@angular/material/button";

export class MondayFirstDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    // 1 = Monday, 0 = Sunday
    return 1;
  }
}

@Component({
  selector: 'feedback-form-declined',
  templateUrl: './feedback-form-declined.component.html',
  styleUrls: ['./feedback-form-declined.component.scss'],
  imports: [
    TranslateModule,
    NgIf,
    FormsModule,
    MatOption,
    MatSelect,
    MatInput,
    MatDatepicker,
    MatDatepickerInput,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatFormField,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatIcon,
    NgClass,
    MatButton
  ],
  providers: [
    {provide: DateAdapter, useClass: MondayFirstDateAdapter},
    {provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS},
    {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: 'outline', floatLabel: 'always', subscriptSizing: 'dynamic'}
    },
    {provide: DatePipe}
  ],
  standalone: true
})
export class FeedbackFormDeclinedComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() hideSendButton: boolean = false;
  @Output() formControlOutput = new EventEmitter<FormGroup>();
  @Output() outputData = new EventEmitter<CommentModel>();
  @Output() sendForm = new EventEmitter<boolean>();
  @ViewChild(MatSelect) dropdown!: MatSelect;
  dropdownOpened = false;
  selected = '';
  showDatepicker: boolean = false;
  sendFormClicked: boolean = false;
  minDate: Date;
  form!: FormGroup;
  dropdownCtrl!: FormControl;
  datepickerCtrl!: FormControl;
  textareaCtrl!: FormControl;
  textareaValueLength: number = 0;
  private dropdownSubscription?: Subscription;
  private readonly _adapter = inject<DateAdapter<unknown, unknown>>(DateAdapter);
  private readonly _locale = signal(inject<unknown>(MAT_DATE_LOCALE));
  private readonly translateService = inject(TranslateService);

  constructor(private fb: FormBuilder, private iconRegistry: MatIconRegistry,
              private sanitizer: DomSanitizer) {
    this.iconRegistry.addSvgIcon(
      'calendar',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/icon-calendar.svg')
    );
    const today = new Date();
    this.minDate = new Date(today.setDate(today.getDate() + 7));
  }

  // Filter function: allow only Monday–Friday
  excludeWeekends = (d: Date | null): boolean => {
    const date = d || new Date();
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    return day !== 0 && day !== 6;
  };

  ngOnInit() {
    this._locale.set(this.translateService.currentLang);
    this._adapter.setLocale(this._locale());

    this.form = this.fb.group({
      dropdown: ['', Validators.required],
      textarea: ['', Validators.required],
      datepicker: [''],
    });

    this.dropdownCtrl = this.form.get('dropdown') as FormControl;
    this.datepickerCtrl = this.form.get('datepicker') as FormControl;
    this.textareaCtrl = this.form.get('textarea') as FormControl;

    this.form.get('dropdown')!.valueChanges.subscribe((res: any) => {
      const datepicker = this.form.get('datepicker');
      const textarea = this.form.get('textarea');
      if (res == 'OTHER_DATE') {
        this.showDatepicker = true;
        datepicker?.setValidators(Validators.required);
        textarea?.clearValidators();
      } else {
        this.showDatepicker = false;
        datepicker?.clearValidators();
        textarea?.setValidators(Validators.required);
      }
      datepicker?.updateValueAndValidity();
      textarea?.updateValueAndValidity();
    });

    this.form.valueChanges.subscribe(value => {
      this.formControlOutput.emit(this.form);

      if (this.form.valid) {
        const {dropdown, textarea, datepicker} = this.form.value;
        const message: CommentModel = {
          typeOfRequestKey: dropdown,
          typeOfRequest: this.translateService.instant('APPOI.FEEDBACK_FORM.TYPE_OF_REQUEST.OPTION_' + dropdown),
          description: textarea,
          desiredDate: datepicker
        }
        this.outputData.emit(message);
      }
    });

    this.form.get('textarea')!.valueChanges.subscribe((res: any) => {
      const textarea = this.form.get('textarea');
      this.textareaValueLength = textarea?.value?.length || 0;
    });
  }

  ngAfterViewInit() {
    this.dropdownSubscription = this.dropdown.openedChange.subscribe(opened => this.dropdownOpened = opened);
  }

  ngOnDestroy() {
    if (this.dropdownSubscription) {
      this.dropdownSubscription.unsubscribe();
    }
  }

  isTextAreaValueLengthValid(): boolean {
    return this.textareaValueLength > 0;
  }

  isRequired(field: string): boolean {
    return this.form.get(field)?.hasValidator(Validators.required) ?? false;
  }

  send(): void {
    this.sendFormClicked = true;
    if (this.form.valid) {
      this.sendForm.emit(true);
    }
  }
}
